/**
 * @author: Claude Code
 * @date: 2026/3/16
 * @desc: PC Console Games 异动指标工具函数
 */
import { SortType } from '@home/enums/common';
import { generateDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';

import type { ISelectOptionWithGroup } from '@intelligence/types/common';

interface ISurgeMetricConfig {
  pageMetricKey: string;
  order_source: string;
  order_metric: string;
}

/**
 * PC Console 异动指标配置
 * key: 异动指标标识
 * value: 页面指标key + 排序参数
 */
export const PC_CONSOLE_SURGE_METRIC_CONFIG_MAP: Record<string, ISurgeMetricConfig> = {
  revenue: {
    pageMetricKey: generateDataSourceMetricKey('revenue', 'digitalRevenue'),
    order_source: 'mscience',
    order_metric: 'digitalRevenue',
  },
  units: {
    pageMetricKey: generateDataSourceMetricKey('revenue', 'units'),
    order_source: 'mscience',
    order_metric: 'units',
  },
  pcu: {
    pageMetricKey: generateDataSourceMetricKey('steamData', 'source_steam__pcu'),
    order_source: 'source_steam',
    order_metric: 'source_steam__pcu',
  },
  dau: {
    pageMetricKey: generateDataSourceMetricKey('dau', 'ampere_dau'),
    order_source: 'ampere',
    order_metric: 'ampere_dau',
  },
  mau: {
    pageMetricKey: generateDataSourceMetricKey('mau', 'ampere_mau'),
    order_source: 'ampere',
    order_metric: 'ampere_mau',
  },
};

// AU异动指标映射 - PC Console 周粒度不显示 AU 指标
const auSurgeMetricMap: IRecord<ISelectOptionWithGroup | null> = {
  daily: { value: 'dau', label: 'dbi_surge_metric_dau', text: 'dbi_surge_metric_dau' },
  monthly: { value: 'mau', label: 'dbi_surge_metric_mau', text: 'dbi_surge_metric_mau' },
  weekly: null, // 周粒度不显示 AU 指标
};

/**
 * 获取 PC Console 异动指标选项
 * @param granularity - 日期粒度: 'daily' | 'weekly' | 'monthly'
 * @returns 异动指标选项数组
 */
export const getPCConsoleSurgeMetricOptions = (granularity?: string): ISelectOptionWithGroup[] => {
  const baseOptions: ISelectOptionWithGroup[] = [
    { value: 'revenue', label: 'dbi_surge_metric_revenue', text: 'dbi_surge_metric_revenue' },
    { value: 'units', label: 'dbi_surge_metric_units', text: 'dbi_surge_metric_units' },
    { value: 'pcu', label: 'dbi_surge_metric_pcu', text: 'dbi_surge_metric_pcu' },
  ];

  // 根据粒度添加对应的 AU 指标
  const auSurgeMetric = granularity ? auSurgeMetricMap[granularity] : null;
  if (auSurgeMetric) {
    return [...baseOptions, auSurgeMetric];
  }

  return baseOptions;
};

/**
 * 获取异动指标配置
 * @param surgeMetric - 异动指标标识
 * @returns 对应配置
 */
export const getSurgeMetricConfig = (surgeMetric: string): ISurgeMetricConfig | undefined => {
  return PC_CONSOLE_SURGE_METRIC_CONFIG_MAP[surgeMetric];
};

/**
 * 判断 source_metrics 中是否包含指定的数据源和指标组合
 * @param sourceMetrics - 当前请求使用的 source_metrics
 * @param orderSource - 数据源
 * @param orderMetric - 指标
 * @returns 是否包含该数据源和指标组合
 */
export const hasSourceMetricPair = (
  sourceMetrics: Record<string, string[]> = {},
  orderSource?: string,
  orderMetric?: string,
): boolean => {
  if (!orderSource || !orderMetric) {
    return false;
  }

  const sourceMetricValues = sourceMetrics?.[orderSource] || [];

  return Array.isArray(sourceMetricValues) && sourceMetricValues.includes(orderMetric);
};

/**
 * 校验异动指标是否仍被当前 source_metrics 支持
 * @param surgeMetric - 异动指标标识
 * @param sourceMetrics - 当前请求使用的 source_metrics
 * @returns 可用的异动指标，不可用则返回空字符串
 */
export const getValidSurgeMetric = (surgeMetric: string, sourceMetrics: Record<string, string[]> = {}): string => {
  if (!surgeMetric) {
    return '';
  }

  const surgeMetricConfig = getSurgeMetricConfig(surgeMetric);

  if (!hasSourceMetricPair(sourceMetrics, surgeMetricConfig?.order_source, surgeMetricConfig?.order_metric)) {
    return '';
  }

  return surgeMetric;
};

/**
 * 判断当前勾选指标中是否包含异动指标对应的数据源和指标
 * @param surgeMetric - 异动指标标识
 * @param selectedMetricValues - 当前勾选的指标 values
 * @param cascaderMap - 指标值到数据源/指标信息的映射
 * @returns 是否已包含对应异动指标
 */
export const hasSelectedSurgeMetric = (
  surgeMetric: string,
  selectedMetricValues: string[] = [],
  cascaderMap: Record<string, any> = {},
): boolean => {
  if (!surgeMetric || !Array.isArray(selectedMetricValues) || selectedMetricValues.length === 0) {
    return false;
  }

  const surgeMetricConfig = getSurgeMetricConfig(surgeMetric);

  if (!surgeMetricConfig?.order_source || !surgeMetricConfig?.order_metric) {
    return false;
  }

  return selectedMetricValues.some((selectedMetricValue) => {
    const selectedMetric = cascaderMap?.[selectedMetricValue];

    return (
      selectedMetric?.dataSource === surgeMetricConfig.order_source &&
      selectedMetric?.value === surgeMetricConfig.order_metric
    );
  });
};

/**
 * 生成异动指标对应的 rotation_order_by 参数
 * @param surgeMetric - 异动指标标识
 * @returns rotation_order_by 参数
 */
export const getSurgeMetricRotationOrderBy = (surgeMetric: string) => {
  const surgeMetricConfig = getSurgeMetricConfig(surgeMetric);

  if (!surgeMetricConfig) {
    return undefined;
  }

  return {
    order_source: surgeMetricConfig.order_source,
    order_metric: surgeMetricConfig.order_metric,
    order: SortType.Desc,
    ratio_type: 0,
    top_size: 500,
  };
};
